import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { getTrainingTopics } from "api/trainingTopics";

const TrainingTopicsContext = createContext(null);

export function TrainingTopicsProvider({ children }) {
  const [sectorTrainingTopics, setSectorTrainingTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all training topics from the API and group by sector_name
  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTrainingTopics();
      const list = result.data || [];
      const grouped = {};
      list.forEach((t) => {
        const sectorKey = t.sector_name || "unknown";
        if (!grouped[sectorKey]) grouped[sectorKey] = [];
        grouped[sectorKey].push({
          id: t.id,
          name: t.name,
          definition: t.definition || "",
          sector_name: t.sector_name,
          start_date: t.start_date,
          iconColor: "text-blue-500",
          created_at: t.created_at,
          updated_at: t.updated_at,
        });
      });
      setSectorTrainingTopics(grouped);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load training topics");
      setSectorTrainingTopics({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // Get topics for a specific sector (by sector name or ID)
  const getTopicsForSector = useCallback(
    (sectorKey) => sectorTrainingTopics[sectorKey] || [],
    [sectorTrainingTopics]
  );

  // Add a new topic to a sector (local state only, after API creation)
  const addTopic = useCallback((sectorKey, topic) => {
    setSectorTrainingTopics((prev) => ({
      ...prev,
      [sectorKey]: [...(prev[sectorKey] || []), { ...topic, id: topic.id || `tt-${Date.now()}` }],
    }));
  }, []);

  // Update an existing topic in a sector
  const updateTopic = useCallback((sectorKey, topicId, updates) => {
    setSectorTrainingTopics((prev) => ({
      ...prev,
      [sectorKey]: (prev[sectorKey] || []).map((t) =>
        t.id === topicId ? { ...t, ...updates } : t
      ),
    }));
  }, []);

  // Remove a topic from a sector
  const removeTopic = useCallback((sectorKey, topicId) => {
    setSectorTrainingTopics((prev) => ({
      ...prev,
      [sectorKey]: (prev[sectorKey] || []).filter((t) => t.id !== topicId),
    }));
  }, []);

  // Initialize topics for a new sector (no-op if already present)
  const initializeSectorTopics = useCallback((sectorKey) => {
    setSectorTrainingTopics((prev) => {
      if (prev[sectorKey]) return prev;
      return { ...prev, [sectorKey]: [] };
    });
  }, []);

  // Get all sector keys that have training topics
  const sectorIds = useMemo(() => Object.keys(sectorTrainingTopics), [sectorTrainingTopics]);

  const value = {
    sectorTrainingTopics,
    getTopicsForSector,
    addTopic,
    updateTopic,
    removeTopic,
    initializeSectorTopics,
    sectorIds,
    loading,
    error,
    refetchTopics: fetchTopics,
  };

  return (
    <TrainingTopicsContext.Provider value={value}>
      {children}
    </TrainingTopicsContext.Provider>
  );
}

export function useTrainingTopics() {
  const ctx = useContext(TrainingTopicsContext);
  if (!ctx) throw new Error("useTrainingTopics must be used within TrainingTopicsProvider");
  return ctx;
}

export default TrainingTopicsContext;
